import {
    useCallback,
} from 'react'
import noop from 'lodash/noop'
import {
    useQueryClient,
} from '@tanstack/react-query'
import ConfirmationDialog from 'app/shared-components/ConfirmationDialog'
import {
    LocationExpandedViewObjectSelector,
} from 'app/shared-components/LocationSelector'
import {
    MultipleContactObjectSelector,
} from 'app/shared-components/MultipleContactSelector'
import useLabel from 'app/hooks/useLabel'
import useSimpleApiRequest from 'app/hooks/useSimpleApiRequest'
import ContactRequests from 'app/requests/Contact.requests'
import {
    RequestKeys,
} from 'app/hooks/useRequest'
import {
    UpdateProspectContactData,
} from './UpdateProspectContactPopup.types'
import useStyles from './UpdateProspectContactPopup.style'

export default function UpdateProspectContactPopup({
    data, open, close,
}: Readonly<{
    data: UpdateProspectContactData,
    open: boolean,
    close: () => void,
}>) {
    const {
        classes,
    } = useStyles()

    const queryClient = useQueryClient()
    const invalidateContacts = useCallback(() => {
        queryClient.invalidateQueries({
            queryKey: [RequestKeys.getContactByIds],
        })
    }, [queryClient])

    const {
        mutate: updateLocationForProspectContacts,
    } = useSimpleApiRequest<UpdateProspectContactData>({
        onSuccess: () => {
            invalidateContacts()
            close()
        },
        name: 'updateLocationForProspectContacts',
        requestParams: ContactRequests.unprospectData(),
        title: 'Contacts were updated with location',
    })

    const label = useLabel()
    const handleClose = useCallback((positive: boolean) => {
        if (!positive) {
            close()
            return
        }
        updateLocationForProspectContacts(data)
    }, [
        close,
        data,
        updateLocationForProspectContacts,
    ])

    return (
        <ConfirmationDialog
            open={open}
            handleClose={handleClose}
            dialogTitle="Associate contacts with location"
            positiveLabel="Confirm"
            negativeLabel="Cancel"
            dialogContent="Prospect contacts can not be associated with Awarded lanes. Please confirm if you agree to associate the following contacts with the selected location."
            classNames={{
                content: classes.content,
            }}
        >
            <div className={classes.container}>
                <LocationExpandedViewObjectSelector
                    name="collectionDropOffPoint"
                    value={data?.location}
                    title={label('LaneGroup.collectionDropoffPoint')}
                    disabled
                    onChange={noop}
                />
                <MultipleContactObjectSelector
                    name="collectionDropOffContacts"
                    value={data?.contacts}
                    title={label('AccountLane.collectionDropoffPointContacts')}
                    disabled
                    onChange={noop}
                />
            </div>
        </ConfirmationDialog>
    )
}
